/**
 * @fileoverview Sound effects service implementing IAudioService.
 * Manages game interaction sound effects (success, celebration, wrong, selection).
 */

import { CONFIG } from '../core/GameConfig.js';
import { IAudioService } from './IAudioService.js';
import { BackgroundMusicService } from './BackgroundMusicService.js';
import { KonvaEventReal } from '../konva-extensions.js';

/**
 * Sound effects service for game events.
 * Handles loading, playing, and managing sound effect variations.
 */
export class SoundEffectsService implements IAudioService {
    private sounds: Map<string, HTMLAudioElement[]> = new Map();
    private enabled: boolean = true;
    private volume: number = 0.5;
    private backgroundMusic: BackgroundMusicService | null = null;
    private audioUnlocked: boolean = false;
    private _isMuted: boolean = false;
    
    /**
     * Set the background music service for coordination.
     * @param {BackgroundMusicService} bgMusic - Background music service
     */
    setBackgroundMusic(bgMusic: BackgroundMusicService): void {
        this.backgroundMusic = bgMusic;
    }
    
    /**
     * Initialize sound effects by preloading audio files.
     */
    init(): void {
        if (!CONFIG.AUDIO.ENABLED) {
            this.enabled = false;
            return;
        }
        
        // Preload all sound effects
        this.loadSounds('success', ['/sounds/success/success1.mp3']);
        this.loadSounds('celebration', ['/sounds/celebration/celebration1.mp3']);
        this.loadSounds('wrong', ['/sounds/wrong/wrong1.mp3']);
        this.loadSounds('selection', ['/sounds/touch/selection1.mp3']);
        
        console.log('Sound effects initialized');
    }
    
    /**
     * Load a category of sounds.
     * @param {string} category - Sound category name
     * @param {string[]} paths - Array of sound file paths
     */
    private loadSounds(category: string, paths: string[]): void {
        const audioElements: HTMLAudioElement[] = [];
        
        for (const path of paths) {
            try {
                const audio = new Audio(path);
                audio.volume = this.volume;
                audio.preload = 'auto'; // Browser will preload automatically
                audioElements.push(audio);
            } catch (e) {
                console.log(`Failed to load sound: ${path}`, e);
            }
        }
        
        if (audioElements.length > 0) {
            this.sounds.set(category, audioElements);
        }
    }
    
    /**
     * Unlock all sound effects on iOS by attempting to play and immediately pause.
     * Must be called from a user gesture event.
     */
    private unlockAudio(): void {
        if (!this.enabled) return;
        if (this.audioUnlocked) return; // Already unlocked
        
        console.log('Unlocking sound effects for iOS...');
        this.audioUnlocked = true;
        
        // Attempt to play and immediately pause each sound to unlock it
        // Must happen synchronously within user gesture for iOS
        this.sounds.forEach((audioArray, category) => {
            audioArray.forEach((audio) => {
                try {
                    // Set volume very low instead of muting (muted doesn't unlock on some iOS versions)
                    const originalVolume = audio.volume;
                    audio.volume = 0.01;
                    
                    // Play synchronously (don't wait for promise)
                    const playPromise = audio.play();
                    
                    // Pause immediately (synchronously)
                    audio.pause();
                    audio.currentTime = 0;
                    audio.volume = originalVolume;
                    
                    console.log(`Unlocked sound: ${category}`);
                    
                    // Handle promise rejection silently
                    if (playPromise !== undefined) {
                        playPromise.catch((err: any) => {
                            console.log(`Unlock ${category} promise rejected (expected):`, err);
                        });
                    }
                } catch (e) {
                    console.log(`Error unlocking ${category}:`, e);
                }
            });
        });
    }
    
    /**
     * Try to unlock audio from a trusted user event.
     * @param {any} evt - The originating user event
     * @returns {boolean} True if unlock was attempted
     */
    tryUnlockFromEvent(evt: KonvaEventReal): boolean {
        // Check if event is trusted (from actual user interaction)
        try {
            if (evt && evt.isTrusted === false) {
                return false;
            }
        } catch (e) {
            // ignore
        }
        
        if (!this.audioUnlocked) {
            this.unlockAudio();
        }
        
        return true;
    }
    
    /**
     * Play a sound from a category.
     * If multiple sounds exist in category, picks randomly.
     * @param {string} category - Sound category to play
     */
    private playSound(category: string): void {
        if (!this.enabled || this._isMuted) return;
        
        const categoryAudios = this.sounds.get(category);
        if (!categoryAudios || categoryAudios.length === 0) {
            console.log(`No sounds found for category: ${category}`);
            return;
        }
        
        // Pick random sound from category (for variety if multiple exist)
        const randomIndex = Math.floor(Math.random() * categoryAudios.length);
        const audio = categoryAudios[randomIndex];
        
        // Reset and play
        try {
            audio.currentTime = 0;
            const playPromise = audio.play();
            
            if (playPromise !== undefined) {
                playPromise.catch((err: any) => {
                    console.log(`Failed to play ${category} sound:`, err);
                });
            }
        } catch (e) {
            console.log(`Error playing ${category} sound:`, e);
        }
    }
    
    /**
     * Play sound (generic method for IAudioService interface).
     * Plays the 'selection' sound by default.
     */
    play(): void {
        this.playSelection();
    }
    
    /**
     * Pause is not applicable to sound effects (they're short).
     * Included for IAudioService interface compliance.
     */
    pause(): void {
        // No-op for sound effects
    }
    
    /**
     * Resume is not applicable to sound effects.
     * Included for IAudioService interface compliance.
     */
    resume(): void {
        // No-op for sound effects
    }
    
    /**
     * Toggle mute state for sound effects.
     * @returns {boolean} New mute state
     */
    toggleMute(): boolean {
        this._isMuted = !this._isMuted;
        return this._isMuted;
    }
    
    /**
     * Check if sound effects are playing.
     * Always returns false since effects are momentary.
     * @returns {boolean} False
     */
    isPlaying(): boolean {
        return false;
    }
    
    /**
     * Check if sound effects are muted.
     * @returns {boolean} True if muted
     */
    isMuted(): boolean {
        return this._isMuted;
    }
    
    /**
     * Play success sound (correct match).
     */
    playSuccess(): void {
        this.playSound('success');
    }
    
    /**
     * Play celebration sound (game complete).
     * Pauses background music during celebration, then resumes.
     */
    playCelebration(): void {
        // Pause background music for dramatic effect
        if (this.backgroundMusic) {
            this.backgroundMusic.pause();
        }
        
        // Get celebration sound
        const categoryAudios = this.sounds.get('celebration');
        if (!this.enabled || this._isMuted || !categoryAudios || categoryAudios.length === 0) {
            // Resume music even if we can't play sound
            if (this.backgroundMusic) {
                this.backgroundMusic.resume();
            }
            return;
        }
        
        const randomIndex = Math.floor(Math.random() * categoryAudios.length);
        const audio = categoryAudios[randomIndex];
        
        // Resume background music when celebration ends
        audio.onended = () => {
            if (this.backgroundMusic) {
                this.backgroundMusic.resume();
            }
        };
        
        // Play celebration sound
        try {
            audio.currentTime = 0;
            const playPromise = audio.play();
            
            if (playPromise !== undefined) {
                playPromise.catch((err: any) => {
                    console.log('Failed to play celebration sound:', err);
                    // Resume music even if celebration failed
                    if (this.backgroundMusic) {
                        this.backgroundMusic.resume();
                    }
                });
            }
        } catch (e) {
            console.log('Error playing celebration sound:', e);
            // Resume music even if celebration failed
            if (this.backgroundMusic) {
                this.backgroundMusic.resume();
            }
        }
    }
    
    /**
     * Play wrong sound (incorrect match).
     */
    playWrong(): void {
        this.playSound('wrong');
    }
    
    /**
     * Play selection sound (shape touched/picked up).
     */
    playSelection(): void {
        this.playSound('selection');
    }
    
    /**
     * Set volume for all sound effects.
     * @param {number} volume - Volume level (0.0 to 1.0)
     */
    setVolume(volume: number): void {
        this.volume = Math.max(0, Math.min(1, volume));
        
        // Update volume for all loaded sounds
        this.sounds.forEach((audioArray) => {
            audioArray.forEach((audio) => {
                audio.volume = this.volume;
            });
        });
    }
    
    /**
     * Enable or disable sound effects.
     * @param {boolean} enabled - Whether to enable sounds
     */
    setEnabled(enabled: boolean): void {
        this.enabled = enabled;
    }
}
